import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { encodeId } from '../../global/Globalfunction';
import { getData, postData } from '../../global/Globalapi';
import Fuse from "fuse.js";
import axiosClient from '../../global/Globalapi';

const baseURL = axiosClient.defaults.baseURL;

const EnhancedSearchBar = () => {
  const [term, setTerm] = useState('');
  const [result, setResult] = useState([]);
  const [isDropOpen, setIsDropOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const [allProducts, setAllProducts] = useState([]);
  const fuseRef = useRef(null);

  const navigate = useNavigate();
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const response = await getData('/search/all');

        if (response?.data?.data) {
          setAllProducts(response.data.data);

          fuseRef.current = new Fuse(response.data.data, {
            keys: ["product_name", "description"],
            threshold: 0.4,
          });
        }
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };

    fetchAllProducts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropOpen(false);
        setSelectedIndex(-1);
      }
    };

    if (isDropOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropOpen]);

  const handleSearchAPI = useCallback((searchTerm) => {
    if (!searchTerm.trim()) {
      setResult([]);
      setIsDropOpen(false);
      setSearchLoading(false);
      return;
    }

    if (!fuseRef.current) return;

    setSearchLoading(true);

    const fuseResults = fuseRef.current.search(searchTerm);
    const items = fuseResults.map(r => r.item);

    setResult(items);
    setIsDropOpen(true);
    setSearchLoading(false);
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (!term.trim()) {
      setResult([]);
      setIsDropOpen(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      handleSearchAPI(term);
    }, 500);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [term, handleSearchAPI]);

  const handleProductClick = useCallback((item) => {
    try {
      let prev = [];
      try {
        const raw = localStorage.getItem('prev');
        prev = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(prev)) prev = [];
      } catch {
        prev = [];
      }

      prev.push(item.product_id);
      if (prev.length > 3) prev = prev.slice(-3);
      localStorage.setItem('prev', JSON.stringify(prev));

      navigate(`/product/${encodeId(item.product_id)}`);

      setIsDropOpen(false);
      setTerm('');
      setSelectedIndex(-1);
    } catch (error) {
      console.error("Error handling product click:", error);
    }
  }, [navigate]);

  const handleViewAllResults = () => {
    if (term.trim() !== '') {
      navigate(`/shopping?search=${encodeURIComponent(term.trim())}`);
      setIsDropOpen(false);
      setTerm('');
      setSelectedIndex(-1);
    }
  };

  const handleKeyDown = (e) => {
    if (!isDropOpen || result.length === 0) {
      if (e.key === 'Enter') handleViewAllResults();
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < result.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleProductClick(result[selectedIndex]);
        } else {
          handleViewAllResults();
        }
        break;
      case 'Escape':
        setIsDropOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const calculateDiscount = (mrp, netPrice) => {
    if (!mrp || !netPrice || mrp <= 0) return 0;
    return Math.round(((mrp - netPrice) / mrp) * 100);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };
const FALLBACK_IMAGE = "/no-image.png";

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center border border-gray-300 rounded-full overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-gray-50">
        <div className="pl-3 pr-2 flex items-center justify-center">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
         </div>
        {/* <input
          ref={inputRef}
          className="px-2 py-3 w-64 md:w-80 text-sm focus:outline-none bg-transparent"
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search products, categories, colors..."
        /> */}
        <input
  ref={inputRef}
  className="
    px-2 py-3
    w-36 sm:w-56 md:w-80
      text-xs sm:text-sm md:text-sm
    focus:outline-none
    bg-transparent
  "
  type="text"
  value={term}
  onChange={(e) => setTerm(e.target.value)}
  onKeyDown={handleKeyDown}
  placeholder="Search products, categories, colors..."
/>

        <button
          onClick={handleViewAllResults}
          disabled={searchLoading || !term.trim()}
          className="bg-black px-4 py-3 text-sm text-white disabled:opacity-50 hover:bg-gray-800 transition-colors"
        >
          {searchLoading ? (
            <span className="flex items-center gap-1">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>    
            </span>
          ) : (
            'Search'
          )}
        </button>
      </div>

      {isDropOpen && (
        <div className="absolute top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-2xl w-full md:w-[500px] z-50 max-h-[500px] overflow-y-auto">
          
          {searchLoading ? (
            <div className="px-4 py-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-gray-600">Searching across products...</p>
            </div>
          ) : result.length > 0 ? (
            <>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium flex justify-between items-center">
                <span>Found {result.length} result{result.length !== 1 ? 's' : ''} for "{term}"</span>
                <button
                  onClick={handleViewAllResults}
                  className="text-white-600 hover:text-orange-600 font-semibold"
                >
                  View All →
                </button>
              </div>

              {result.slice(0, 5).map((item, index) => {
                const imagePath = item.file_name
                  ? `${baseURL}/uploads/${item.file_name}`
                  // : 'https://via.placeholder.com/80x80?text=No+Image';
                   : FALLBACK_IMAGE;
console.log("baseURL:", baseURL);
console.log("file_name:", item.file_name);

                const discount = calculateDiscount(item.mrp_price, item.net_price_rtl);

                return (
                  <div
                    key={`${item.product_id}-${index}`}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 ${
                      selectedIndex === index ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleProductClick(item)}
                  >
                    <img
                      src={imagePath}
                      className="h-16 w-16 object-cover rounded-md flex-shrink-0 border border-gray-200"
                      alt={item.product_name || 'Product'}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                      }}
                    />

                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">
                        {item.product_name || 'Unnamed Product'}
                      </h4>

                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.category_name && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {item.category_name}
                          </span>
                        )}
                        {item.parent_category_name && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                            {item.parent_name}
                          </span>
                        )}
                        {item.product_type_name && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                            {item.product_type_name}
                          </span>
                        )}
                      </div>

                      {item.attributes && (
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          {item.attributes}
                        </p>
                      )}

                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-bold text-gray-900">
                          {formatPrice(item.net_price_rtl)}
                        </span>
                        {discount > 0 && (
                          <>
                            <span className="text-xs text-gray-500 line-through">
                              {formatPrice(item.mrp_price)}
                            </span>
                            <span className="text-xs font-semibold text-green-600">
                              {discount}% OFF
                            </span>
                          </>
                        )}
                      </div>

                      <div className="flex gap-1 mt-1">
                        {item.Feature_product === 1 && (
                          <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                            ⭐ Featured
                          </span>
                        )}
                        {item.New_Product === 1 && (
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded">
                            🆕 New
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {result.length > 5 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                  <button
                    onClick={handleViewAllResults}
                    className="w-full  bg-[#c97c5d]  hover:bg-orange-700 text-white py-2 rounded-lg font-semibold transition"
                  >
                    View All {result.length} Results
                  </button>
                </div>
              )}

              <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 text-center">
                Use ↑↓ to navigate, Enter to select or view all
              </div>
            </>
          ) : (
            <div className="px-4 py-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="mt-2 text-gray-600">No results found for "{term}"</p>
              <p className="mt-1 text-xs text-gray-500">Try searching by product name, category, color, or type</p>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default EnhancedSearchBar;